/**
 * The `PERSONALIZATION_FIELDS` spec (DEV-573): parsing and composing the
 * values the publisher does not type. Fourtly Showcase reads the same spec in
 * its `src/lib/personalization.js`; keep the two in step.
 */

export interface DeclaredField {
	key: string;
	/** Only set by the JSON shape; `undefined` means "infer it". */
	type?: string;
	/** Friendly label from the object shape; `undefined` falls back to `key`. */
	label?: string;
	/** `{KEY}` template composing this field on submit (DEV-673). */
	derivedFrom?: string;
	/** Key of another field; this one is `'true'` when that one is filled (DEV-1064). */
	trueIfFilled?: string;
	/** Hide the input. Defaults to true for computed fields, false otherwise. */
	hidden?: boolean;
}

/** The part of a resolved field that decides how its value is computed. */
export interface ComputedFieldSpec {
	key: string;
	derivedFrom?: string;
	trueIfFilled?: string;
}

/**
 * Parse `PERSONALIZATION_FIELDS` in either the JSON or comma-separated shape.
 *
 * In the JSON shape a field's value is normally a type string (`"text"`,
 * `"image"`); an object value declares more: `label`, a `derivedFrom`
 * template, or `trueIfFilled`, both of which make the field computed rather
 * than entered.
 */
export function parseDeclaredFields(raw: string): DeclaredField[] {
	if (raw.startsWith('{')) {
		try {
			const parsed: unknown = JSON.parse(raw);
			if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
				return Object.entries(parsed as Record<string, unknown>).map(([rawKey, spec]) => {
					const key = rawKey.trim().toUpperCase();
					if (spec && typeof spec === 'object' && !Array.isArray(spec)) {
						const o = spec as Record<string, unknown>;
						const derivedFrom = typeof o['derivedFrom'] === 'string' ? o['derivedFrom'] : undefined;
						const trueIfFilled = typeof o['trueIfFilled'] === 'string' ? o['trueIfFilled'].trim().toUpperCase() : undefined;
						return {
							key,
							type: typeof o['type'] === 'string' ? o['type'].trim().toLowerCase() : undefined,
							label: typeof o['label'] === 'string' ? o['label'] : undefined,
							derivedFrom,
							trueIfFilled,
							// Computed fields are never entered — hide by default.
							hidden: typeof o['hidden'] === 'boolean'
								? o['hidden']
								: derivedFrom !== undefined || trueIfFilled !== undefined,
						};
					}
					return {
						key,
						type: typeof spec === 'string' ? spec.trim().toLowerCase() : undefined,
					};
				});
			}
		} catch {
			// A stray brace must not empty the dialog — fall through to the list parser.
		}
	}
	return raw
		.split(',')
		.map(k => k.trim().toUpperCase())
		.filter(Boolean)
		.map(key => ({ key }));
}

/**
 * Fold computed fields into the submitted values. `derivedFrom` interpolates
 * `{KEY}` from what the publisher entered, with whitespace collapsed so an
 * empty last name leaves no trailing space. `trueIfFilled` becomes `'true'` or
 * `'false'`; the backend coerces it to the arg's declared type.
 */
export function composePersonalizationFields(
	fields: readonly ComputedFieldSpec[],
	entered: Record<string, string>,
): Record<string, string> {
	const out: Record<string, string> = { ...entered };
	for (const field of fields) {
		if (field.derivedFrom) {
			out[field.key] = field.derivedFrom
				.replace(/\{(\w+)\}/g, (_, k: string) => entered[k.toUpperCase()] ?? '')
				.replace(/\s+/g, ' ')
				.trim();
		} else if (field.trueIfFilled) {
			out[field.key] = String((entered[field.trueIfFilled] ?? '').trim() !== '');
		}
	}
	return out;
}
